import React from 'react';
import { useTranslation } from 'react-i18next';
import { WizardStepProps } from '../types/wizard.types';
import { cn } from '@/shared/lib/utils';
import { ComponentReturnTypeMap } from '@momentum/types';

interface ChoiceOption {
  id: string;
  title: string;
  description?: string;
  value: number;
  color?: {
    from: string; // Hex color (e.g., '#4ade80')
    to: string; // Hex color (e.g., '#16a34a')
    ring?: string; // Hex color (e.g., '#16a34a')
  };
}

interface WizardRangeStepProps
  extends WizardStepProps<ComponentReturnTypeMap['Range']> {
  options?: ChoiceOption[];
  autoAdvance?: boolean;
  colorTheme?: 'positive' | 'negative'; // positive = green, negative = red
}

export const WizardRangeStep: React.FC<WizardRangeStepProps> = ({
  value,
  onChange,
  onNext,
  onPrevious: _onPrevious,
  error,
  disabled,
  required = false,
  colorTheme = 'positive',
  options,
  autoAdvance = true,
  setSubmitHandler,
}) => {
  const { t } = useTranslation('wizard');

  // Use translated default options if none provided
  const defaultOptions: ChoiceOption[] = [
    {
      id: 'relatively',
      title: t('common.rangeDefaults.relatively'),
      value: 1,
      color: { from: '#a7f3d0', to: '#6ee7b7', ring: '#10b981' },
    },
    {
      id: 'significantly',
      title: t('common.rangeDefaults.significantly'),
      value: 2,
      color: { from: '#34d399', to: '#10b981', ring: '#059669' },
    },
    {
      id: 'extremely',
      title: t('common.rangeDefaults.extremely'),
      value: 3,
      color: { from: '#059669', to: '#047857', ring: '#065f46' },
    },
  ];

  // Register submit handler for the card footer's Next button
  const typedSetSubmitHandler = setSubmitHandler as
    | ((handler: (() => void) | null) => void)
    | undefined;
  if (onNext && !autoAdvance) {
    typedSetSubmitHandler?.(() => onNext(value));
  }

  const rangeOptions = options || defaultOptions;
  const selectedOption = rangeOptions.find((opt) => opt.value === value);

  const handleOptionClick = (option: ChoiceOption) => {
    if (disabled) return;

    onChange(option.value);

    // Auto-advance after selecting a value
    if (autoAdvance && onNext) {
      setTimeout(() => {
        onNext(option.value);
      }, 300); // Small delay for better UX
    }
  };

  const getOptionStyles = (option: ChoiceOption, isSelected: boolean) => {
    const baseClasses =
      'w-full p-6 rounded-3xl font-semibold text-lg transition-all duration-200 cursor-pointer transform hover:scale-[1.02] hover:shadow-md relative overflow-hidden';

    if (disabled) {
      return {
        className: cn(baseClasses, 'opacity-50 cursor-not-allowed'),
        style: {
          '--gradient-from': '#e5e7eb',
          '--gradient-to': '#9ca3af',
        },
      };
    }

    // Use colors from props or fallback based on theme
    const defaultColors =
      colorTheme === 'negative'
        ? { from: '#fed7d7', to: '#e53e3e', ring: '#c53030' } // Red for negative feedback
        : { from: '#34d399', to: '#10b981', ring: '#059669' }; // Green for positive feedback

    const colors = option.color || defaultColors;

    // Colors should be hex values (e.g., '#4ade80') for proper CSS gradient support
    const fromColor = colors.from;
    const toColor = colors.to;
    const ringColor = colors.ring || colors.to;

    if (isSelected) {
      return {
        className: cn(baseClasses, 'shadow-lg ring-4 ring-offset-2'),
        style: {
          '--tw-ring-color': ringColor,
          '--gradient-from': fromColor,
          '--gradient-to': toColor,
          color: ringColor,
        },
      };
    }

    // Unselected state - consistent styling for all options
    return {
      className: cn(baseClasses, 'hover:shadow-lg'),
      style: {
        '--gradient-from': fromColor,
        '--gradient-to': toColor,
        color: '#374151', // Consistent gray text for all unselected options
      },
    };
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="space-y-4 mb-8">
        {rangeOptions.map((option) => {
          const isSelected = selectedOption?.value === option.value;
          const optionStyles = getOptionStyles(option, isSelected);

          return (
            <button
              key={option.id}
              onClick={() => handleOptionClick(option)}
              disabled={disabled}
              className={optionStyles.className}
              style={optionStyles.style}
            >
              {/* Gradient border using pseudo-element */}
              <div
                className="absolute inset-0 rounded-3xl p-0.5"
                style={{
                  background: `linear-gradient(135deg, var(--gradient-from), var(--gradient-to))`,
                }}
              >
                <div className="w-full h-full bg-white rounded-3xl flex flex-col items-center justify-center p-4">
                  <div className="flex items-center justify-center">
                    <span className="text-center font-semibold">
                      {option.title}
                    </span>
                    {isSelected && (
                      <div className="ml-3 flex-shrink-0">
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  {option.description && (
                    <p className="mt-2 text-sm opacity-75 text-center text-gray-600">
                      {option.description}
                    </p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-6">
          <p className="text-sm text-red-600 text-center bg-red-50 p-3 rounded-lg">
            {error}
          </p>
        </div>
      )}

      {!typedSetSubmitHandler && onNext && !autoAdvance && (
        <div className="flex justify-end">
          <button
            onClick={() => onNext(value)}
            disabled={disabled || (required && !value)}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-md hover:shadow-lg"
          >
            {t('common.buttons.next')}
          </button>
        </div>
      )}
    </div>
  );
};

/* 
MongoDB Documents that should use this component:
- All questions with type: "range" (14 documents) - use colorTheme='positive'
- All questions with type: "feedback-range" (6 documents) - use colorTheme='negative'
- Examples: 
  - "To what extent does recipientPlaceholder's praise improve the chances of achieving the goal?" (positive)
  - "To what extent is the value of the organization compromised?" (negative)
  - Range questions for measuring impact levels, satisfaction, organizational impact, etc.
*/
